import { createIcon } from "./icons";

export type DecorPlayButtonPosition = "top-left" | "top-right" | "bottom-left" | "bottom-right";

export interface DecorPlayButtonOptions {
  /** Coin de l'écran où placer le bouton. Par défaut : "bottom-right", au-dessus de `ZoomControl`. */
  position?: DecorPlayButtonPosition;
  /** Fusionné par-dessus le style par défaut — même convention que `ResetViewButtonOptions.style`. */
  style?: Partial<CSSStyleDeclaration>;
  /** Par défaut : "Lancer les animations". */
  playLabel?: string;
  /** Par défaut : "Arrêter les animations". */
  pauseLabel?: string;
}

const POSITION_OFFSETS: Record<DecorPlayButtonPosition, Partial<CSSStyleDeclaration>> = {
  "top-left": { top: "10px", left: "10px" },
  "top-right": { top: "10px", right: "10px" },
  "bottom-left": { bottom: "10px", left: "10px" },
  "bottom-right": { bottom: "10px", right: "10px" },
};

const DEFAULT_STYLE: Partial<CSSStyleDeclaration> = {
  position: "absolute",
  zIndex: "10",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "36px",
  height: "36px",
  padding: "0",
  background: "#fff",
  color: "#444",
  border: "1px solid #ddd",
  borderRadius: "8px",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
  cursor: "pointer",
};

/** Lance et arrête les animations du décor, figées pendant l'édition. Masqué tant qu'aucun élément n'est animé. */
export class DecorPlayButton {
  private el: HTMLButtonElement;
  private visibleDisplay: string;
  private edgeProperty: "top" | "bottom";
  private playing = false;

  constructor(container: HTMLElement, private readonly options: DecorPlayButtonOptions, private readonly onToggle: (playing: boolean) => void) {
    this.el = document.createElement("button");
    this.el.type = "button";
    this.el.className = "toka-decor-play-button";
    Object.assign(this.el.style, DEFAULT_STYLE, POSITION_OFFSETS[options.position ?? "bottom-right"], options.style);
    this.visibleDisplay = this.el.style.display;
    this.edgeProperty = (options.position ?? "bottom-right").startsWith("top") ? "top" : "bottom";
    this.el.addEventListener("click", () => this.setPlaying(!this.playing));
    container.appendChild(this.el);
    this.render();
    this.setVisible(false);
  }

  isPlaying(): boolean {
    return this.playing;
  }

  setPlaying(playing: boolean): void {
    if (playing === this.playing) return;
    this.playing = playing;
    this.render();
    this.onToggle(playing);
  }

  setVisible(visible: boolean): void {
    this.el.style.display = visible ? this.visibleDisplay : "none";
  }

  setStackOffset(offsetPx: number): void {
    this.el.style[this.edgeProperty] = `${offsetPx}px`;
  }

  destroy(): void {
    this.el.remove();
  }

  private render(): void {
    const label = this.playing ? this.options.pauseLabel ?? "Arrêter les animations" : this.options.playLabel ?? "Lancer les animations";
    this.el.replaceChildren(createIcon(this.playing ? "pause" : "play"));
    this.el.setAttribute("aria-label", label);
    this.el.setAttribute("aria-pressed", String(this.playing));
    this.el.title = label;
  }
}
